import { useState } from 'react';
import { BanIcon, LockIcon, LogOutIcon, MessageSquareIcon, SmileIcon } from 'lucide-react';
import { useBackendApi } from '@/hooks/fetch';
import { useAdminPerms } from '@/hooks/auth';
import { cn } from '@/lib/utils';
import type { GenericApiOkResp } from '@shared/genericApiTypes';
import type { CrashMapLivePlayer } from '@shared/vantaCrashMapTypes';

type Action = 'message' | 'kick' | 'ban' | 'jail' | 'troll' | null;

/** actions troll (core crashMap, action 'troll' ; effets dans resource/cl_vanta_troll.lua) */
const TROLLS = [
    ['drunk', 'Drunk', '25 s'],
    ['fire', 'Fire', '6 s'],
    ['animals', 'Wild animals', '30 s'],
    ['ragdoll', 'Ragdoll', '5 s'],
    ['launch', 'Launch', ''],
    ['freeze', 'Freeze', '10 s'],
    ['blackout', 'Black screen', '6 s'],
    ['eject', 'Eject (vehicle)', ''],
    ['tires', 'Burst tires', ''],
    ['engine', 'Kill engine', '12 s'],
    ['slippery', 'Slippery car', '15 s'],
] as const;

const BAN_DURATIONS = [
    ['2 hours', '2 h'],
    ['1 day', '1 day'],
    ['3 days', '3 days'],
    ['1 week', '1 week'],
    ['1 month', '1 month'],
    ['permanent', 'Permanent'],
] as const;
const JAIL_MINUTES = [5, 10, 15, 30, 60, 120];

/**
 * Actions directes depuis le panneau d'écran : message privé (DM txAdmin, affiché en notification en jeu),
 * kick et ban (txAdmin), jail (prison admin de Vanta).
 */
export function ModerationBar({ player, serverMutex }: { player: CrashMapLivePlayer; serverMutex: string | null }) {
    const { hasPerm } = useAdminPerms();
    const [action, setAction] = useState<Action>(null);
    const [reason, setReason] = useState('');
    const [banDuration, setBanDuration] = useState<string>('1 day');
    const [jailMinutes, setJailMinutes] = useState(15);
    const [busy, setBusy] = useState(false);

    const playerRef = serverMutex ? { mutex: serverMutex, netid: player.id } : null;
    const messageApi = useBackendApi<GenericApiOkResp>({ method: 'POST', path: '/player/message' });
    const kickApi = useBackendApi<GenericApiOkResp>({ method: 'POST', path: '/player/kick' });
    const banApi = useBackendApi<GenericApiOkResp>({ method: 'POST', path: '/player/ban' });
    const jailApi = useBackendApi<GenericApiOkResp>({ method: 'POST', path: '/vanta/crashmap/jail' });
    const trollApi = useBackendApi<GenericApiOkResp>({ method: 'POST', path: '/vanta/crashmap/troll' });

    const troll = (kind: string, label: string) => {
        if (busy) return;
        setBusy(true);
        trollApi({
            data: { id: player.id, kind },
            genericHandler: { successMsg: `${label} sent.` },
            success: () => setBusy(false),
            error: () => setBusy(false),
        });
    };

    const reset = () => {
        setAction(null);
        setReason('');
        setBusy(false);
    };
    const toggle = (a: Exclude<Action, null>) => {
        setAction((cur) => (cur === a ? null : a));
        setReason('');
    };

    const submit = () => {
        if (busy || !action) return;
        const trimmed = reason.trim();
        if (action === 'message') {
            if (!playerRef || !trimmed) return;
            setBusy(true);
            messageApi({
                queryParams: playerRef,
                data: { message: trimmed },
                toastLoadingMessage: 'Sending message...',
                genericHandler: { successMsg: 'Message sent.' },
                success: reset,
                error: () => setBusy(false),
            });
        } else if (action === 'kick') {
            if (!playerRef) return;
            setBusy(true);
            kickApi({
                queryParams: playerRef,
                data: { reason: trimmed },
                toastLoadingMessage: 'Kicking player...',
                genericHandler: { successMsg: 'Player kicked.' },
                success: reset,
                error: () => setBusy(false),
            });
        } else if (action === 'ban') {
            if (!playerRef) return;
            setBusy(true);
            banApi({
                queryParams: playerRef,
                data: { reason: trimmed, duration: banDuration },
                toastLoadingMessage: 'Banning player...',
                genericHandler: { successMsg: 'Player banned.' },
                success: reset,
                error: () => setBusy(false),
            });
        } else if (action === 'jail') {
            if (!trimmed) return;
            setBusy(true);
            jailApi({
                data: { id: player.id, minutes: jailMinutes, reason: trimmed },
                toastLoadingMessage: 'Sending to jail...',
                genericHandler: { successMsg: `Jail sent (${jailMinutes} min).` },
                success: reset,
                error: () => setBusy(false),
            });
        }
    };

    const btn = (a: Exclude<Action, null>, label: string, Icon: typeof BanIcon, perm: string, danger = false) => (
        <button
            type="button"
            disabled={!hasPerm(perm) || (a !== 'jail' && a !== 'troll' && !playerRef)}
            onClick={() => toggle(a)}
            className={cn(
                'flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed',
                action === a ? (danger ? 'border-destructive bg-destructive/15 text-destructive' : 'border-primary bg-secondary') : 'hover:bg-muted',
            )}
        >
            <Icon className="size-4" />{label}
        </button>
    );

    return (
        <div className="flex flex-col gap-2">
            <div className="grid grid-cols-5 gap-2">
                {btn('message', 'Message', MessageSquareIcon, 'players.direct_message')}
                {btn('troll', 'Troll', SmileIcon, 'players.troll')}
                {btn('jail', 'Jail', LockIcon, 'players.kick')}
                {btn('kick', 'Kick', LogOutIcon, 'players.kick')}
                {btn('ban', 'Ban', BanIcon, 'players.ban', true)}
            </div>
            {action === 'troll' && (
                <div className="flex flex-col gap-2 rounded-md border p-2.5">
                    <div className="grid grid-cols-2 gap-1.5">
                        {TROLLS.map(([kind, label, dur]) => (
                            <button key={kind} type="button" disabled={busy} onClick={() => troll(kind, label)}
                                className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50">
                                <span>{label}</span>
                                {dur && <span className="text-muted-foreground">{dur}</span>}
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Applied on the player's game within ~1 s. The Vanta anticheat ignores the player for 30 s. Logged in the Action Log.</p>
                </div>
            )}
            {action && action !== 'troll' && (
                <form
                    className="flex flex-col gap-2 rounded-md border p-2.5"
                    onSubmit={(e) => { e.preventDefault(); submit(); }}
                >
                    {action === 'ban' && (
                        <div className="flex flex-wrap gap-1">
                            {BAN_DURATIONS.map(([value, label]) => (
                                <button key={value} type="button" onClick={() => setBanDuration(value)}
                                    className={cn('rounded border px-2 py-0.5 text-xs', banDuration === value ? 'border-destructive text-destructive' : 'text-muted-foreground hover:bg-muted')}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                    {action === 'jail' && (
                        <div className="flex flex-wrap gap-1">
                            {JAIL_MINUTES.map((m) => (
                                <button key={m} type="button" onClick={() => setJailMinutes(m)}
                                    className={cn('rounded border px-2 py-0.5 text-xs', jailMinutes === m ? 'border-primary bg-secondary' : 'text-muted-foreground hover:bg-muted')}>
                                    {m} min
                                </button>
                            ))}
                        </div>
                    )}
                    <input
                        autoFocus
                        className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                        placeholder={action === 'message' ? 'Message (shown in game)' : action === 'jail' ? 'Reason (required)' : 'Reason'}
                        value={reason}
                        maxLength={action === 'message' ? 256 : 120}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="flex gap-2">
                        <button type="button" onClick={reset} className="flex-1 rounded-md border px-2 py-1 text-sm hover:bg-muted">Cancel</button>
                        <button
                            type="submit"
                            disabled={busy || ((action === 'jail' || action === 'message') && !reason.trim())}
                            className={cn('flex-1 rounded-md px-2 py-1 text-sm font-medium disabled:opacity-50',
                                action === 'ban' ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground')}
                        >
                            {action === 'message' ? 'Send' : action === 'kick' ? 'Kick' : action === 'ban' ? `Ban (${banDuration})` : `Jail ${jailMinutes} min`}
                        </button>
                    </div>
                    {action === 'jail' && (
                        <p className="text-[11px] text-muted-foreground">Uses the Vanta admin jail (by unique ID). The player stays connected.</p>
                    )}
                </form>
            )}
        </div>
    );
}
